import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { GeneralIds, ModelTypes } from 'types/types';
import { useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  dialogState,
  toastStateSelector,
  usersAtom,
} from 'state';
import { useParams } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import { SubProject, User } from 'api/models';
import { Add, Delete, Edit, Save } from '@mui/icons-material';
import { useState } from 'react';
import { mutation, query, remove } from 'operations';

type LocalSubProject = SubProject & { isEditing?: boolean };

export function AddSubProjectDialog() {
  const closeDialog = useSetRecoilState(dialogState);
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const [subProjects, setSubProjects] = useState<LocalSubProject[]>([]);
  const setToast = useSetRecoilState(toastStateSelector);
  const setUserStore = useSetRecoilState(usersAtom);

  useQuery<SubProject[]>({
    type: ModelTypes.subProject,
    ids: {
      projectId,
    },
    set: {
      data: setSubProjects,
    },
  });

  return (
    <Box>
      <DialogTitle>{t('dialogs.addSubProject.title')}</DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('dialogs.addSubProject.subProjectName')} *
                </TableCell>
                <TableCell>
                  {t('dialogs.addSubProject.subProjectDescription')}
                </TableCell>
                <TableCell>{t('general.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subProjects?.map(subProject => (
                <TableRow key={subProject.id}>
                  <TableCell component="th" scope="row">
                    <TextField
                      disabled={!subProject.isEditing}
                      type="text"
                      required
                      value={subProject.name}
                      onChange={e => {
                        const newSubProjects = [...subProjects];
                        const index = newSubProjects.findIndex(
                          sp => sp.id === subProject.id,
                        );
                        newSubProjects[index].name = e.target.value;
                        setSubProjects(newSubProjects);
                      }}
                      error={!subProject.name}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      disabled={!subProject.isEditing}
                      type="text"
                      value={subProject.description}
                      onChange={async e => {
                        const newSubProjects = [...subProjects];
                        const index = newSubProjects.findIndex(
                          sp => sp.id === subProject.id,
                        );
                        newSubProjects[index].description = e.target.value;
                        setSubProjects(newSubProjects);
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const newSubProjects = [...subProjects];
                        const index = newSubProjects.findIndex(
                          sp => sp.id === subProject.id,
                        );

                        if (subProject.isEditing) {
                          try {
                            const { data: savedSubProject } =
                              await mutation<SubProject>({
                                type: ModelTypes.subProject,
                                body: {
                                  name: subProject.name,
                                  description: subProject.description ?? '',
                                  id: subProject.id.includes('subProject-')
                                    ? undefined
                                    : subProject.id,
                                } as any,
                                ids: {
                                  projectId,
                                  id: subProject.id.includes('subProject-')
                                    ? undefined
                                    : subProject.id,
                                },
                              });

                            newSubProjects[index] = {
                              isEditing: false,
                              ...savedSubProject!,
                            };

                            const { data: users } = await query<User[]>({
                              type: ModelTypes.user,
                              ids: {
                                projectId,
                              },
                            });
                            setUserStore(users);

                            setToast({
                              isOpen: true,
                              message: t('general.success.save'),
                              severity: ToastSeverity.success,
                            });
                          } catch (e: any) {
                            const errorCode = e.response?.data?.error?.[0].code;
                            setToast({
                              isOpen: true,
                              message: i18n.exists(`errors.${errorCode}`)
                                ? t(`errors.${errorCode}`)
                                : t('general.errors.save'),
                              severity: ToastSeverity.error,
                            });
                          }
                        } else {
                          newSubProjects[index].isEditing =
                            !newSubProjects[index].isEditing;
                        }

                        setSubProjects(newSubProjects);
                      }}
                    >
                      {subProject.isEditing ? (
                        <Tooltip title={t('general.save')}>
                          <Save fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('general.edit')}>
                          <Edit fontSize="small" />
                        </Tooltip>
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        try {
                          if (subProject.id.includes('subProject-')) {
                            return setSubProjects(
                              subProjects.filter(sp => sp.id !== subProject.id),
                            );
                          }
                          remove({
                            type: ModelTypes.subProject,
                            ids: {
                              projectId,
                              id: subProject.id,
                            },
                          });
                          setSubProjects(
                            subProjects.filter(sp => sp.id !== subProject.id),
                          );
                          setToast({
                            isOpen: true,
                            message: t('general.success.delete'),
                            severity: ToastSeverity.success,
                          });
                        } catch (e: any) {
                          const errorCode = e.response?.data?.error?.[0].code;
                          setToast({
                            isOpen: true,
                            message: i18n.exists(`errors.${errorCode}`)
                              ? t(`errors.${errorCode}`)
                              : t('general.errors.save'),
                            severity: ToastSeverity.error,
                          });
                        }
                      }}
                      disabled={
                        subProjects.filter(sp => !sp.id.includes('subProject-'))
                          .length === 1
                      }
                    >
                      <Tooltip title={t('general.delete')}>
                        <Delete fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box width={'100%'} display={'flex'} justifyContent={'flex-end'} mt={2}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setSubProjects([
                ...subProjects,
                {
                  id: `subProject-${subProjects.length + 1}`,
                  name: '',
                  description: '',
                },
              ]);
            }}
          >
            {t('dialogs.addSubProject.addSubProject')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
          onClick={() => closeDialog({ isOpen: false })}
        >
          {t('general.cancel')}
        </Button>
      </DialogActions>
    </Box>
  );
}
